import {
  AlertTriangle, ArrowDownToLine, ArrowUpFromLine, Banknote, Building2, ChevronRight, CircleAlert, FileSearch, Handshake, Images, Info, Map, Package,
  RefreshCw, Settings, ShieldAlert, UserPlus, Users, Wallet
} from "lucide-react";
import { Link } from "react-router-dom";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { money, relativeTime } from "../../lib/format";
import { useAdminOverview } from "../../lib/queries";
import type { AttentionItem } from "../../types";
import { FlowChart } from "./FlowChart";
import { AuditList, PageError, StatTile, compactMoney } from "./shared";

const SEVERITY_ICON = { danger: CircleAlert, warning: AlertTriangle, info: Info } as const;

export function AdminOverviewPage() {
  usePageHeader("Resumen", "Estado de la plataforma, la integración con Kuspit y la alianza con Peso a Peso.");
  const overview = useAdminOverview();
  if (overview.isLoading) return <div className="page-state"><Spinner label="Cargando" /></div>;
  if (overview.isError || !overview.data) return <PageError title="No pudimos cargar el resumen" retry={() => void overview.refetch()} />;
  const { counts, balances, kuspit, lending, attention, series, recentAudit } = overview.data;
  const openLoans = lending.submitted.count + lending.in_review.count;

  return (
    <div className="page-container admin-stack">
      <section className={`attention-card${attention.length ? "" : " calm"}`} aria-labelledby="attention-title">
        <div className="section-heading small">
          <div><span className="page-eyebrow">Hoy</span><h2 id="attention-title">{attention.length ? "Requiere atención" : "Todo en orden"}</h2></div>
          <span>{overview.dataUpdatedAt ? `Actualizado ${relativeTime(new Date(overview.dataUpdatedAt).toISOString())}` : ""}</span>
        </div>
        {attention.length === 0 && <p className="field-hint">No hay descuadres, webhooks fallidos ni solicitudes esperando. El panel se actualiza solo cada 30 segundos.</p>}
        {attention.length > 0 && (
          <ul className="attention-list">
            {attention.map((item) => <AttentionRow key={item.key} item={item} />)}
          </ul>
        )}
      </section>

      <div className="overview-grid">
        <section className="overview-block">
          <span className="rail-title">Clientes</span>
          <div className="stat-grid compact">
            <StatTile label="Clientes" value={counts.users} hint={`${counts.active_users} activos`} icon={Users} to="/admin/usuarios" />
            <StatTile label="Nuevos · 30 días" value={counts.new_users_30d} icon={UserPlus} to="/admin/usuarios" />
            <StatTile label="KYC pendiente" value={counts.pending_kyc} icon={ShieldAlert} to="/admin/usuarios?kyc=pending" tone={counts.pending_kyc > 0 ? "alert" : "default"} />
            <StatTile label="Wallets" value={`${counts.active_wallets} / ${counts.wallets}`} hint={counts.pending_wallets ? `${counts.pending_wallets} en apertura` : "activas / totales"} icon={Wallet} to="/admin/kuspit/contratos" />
          </div>
        </section>
        <section className="overview-block">
          <span className="rail-title">Dinero</span>
          <div className="stat-grid compact">
            <StatTile label="Saldo en custodia" value={compactMoney(balances.total)} hint={balances.interest > 0 ? `${money.format(balances.interest)} en rendimientos · según Kuspit` : "según Kuspit"} icon={Banknote} />
            <StatTile label="Depósitos · 30 días" value={compactMoney(balances.deposits_30d)} hint={`${balances.deposit_count_30d} movimientos`} icon={ArrowDownToLine} />
            <StatTile label="Retiros · 30 días" value={compactMoney(balances.withdrawals_30d)} hint={`${balances.withdrawal_count_30d} movimientos`} icon={ArrowUpFromLine} />
            <StatTile label="Por confirmar" value={compactMoney(balances.pending_in)} hint={balances.pending_out > 0 ? `${money.format(balances.pending_out)} saliendo` : "depósitos en tránsito"} icon={RefreshCw} tone={balances.pending_in > 0 ? "alert" : "default"} to="/admin/kuspit/contratos" />
          </div>
        </section>
      </div>

      <div className="overview-grid">
        <section className="overview-block">
          <div className="section-heading small"><span className="rail-title">Kuspit</span><Link to="/admin/kuspit" className="text-link">Abrir panel <ChevronRight size={14} aria-hidden="true" /></Link></div>
          <div className="stat-grid compact">
            <StatTile label="Contratos" value={kuspit.contracts} hint={`${kuspit.linked} clientes vinculados`} icon={Building2} to="/admin/kuspit/contratos" />
            <StatTile label="Sin saldo de Kuspit" value={kuspit.syncFailed} icon={CircleAlert} tone={kuspit.syncFailed > 0 ? "danger" : "ok"} to="/admin/kuspit/contratos?solo=failed" hint="última lectura falló" />
            <StatTile label="Webhooks fallidos" value={kuspit.failedNotifications} icon={AlertTriangle} tone={kuspit.failedNotifications > 0 ? "danger" : "ok"} to="/admin/kuspit/webhooks?estatus=failed" />
            <StatTile label="Sin sincronizar" value={kuspit.staleWallets} hint="> 30 min" icon={RefreshCw} tone={kuspit.staleWallets > 0 ? "alert" : "ok"} to="/admin/kuspit/contratos?solo=stale" />
          </div>
        </section>
        <section className="overview-block">
          <div className="section-heading small"><span className="rail-title">Peso a Peso</span><Link to="/admin/peso-a-peso" className="text-link">Abrir panel <ChevronRight size={14} aria-hidden="true" /></Link></div>
          <div className="stat-grid compact">
            <StatTile label="Por resolver" value={openLoans} hint={`${lending.submitted.count} nuevas · ${lending.in_review.count} en revisión`} icon={FileSearch} tone={openLoans > 0 ? "alert" : "default"} to="/admin/peso-a-peso/solicitudes" />
            <StatTile label="Aprobadas" value={lending.approved.count} hint={compactMoney(lending.approved.amount)} icon={Handshake} to="/admin/peso-a-peso/solicitudes?estatus=approved" />
            <StatTile label="Rechazadas" value={lending.rejected.count} hint={compactMoney(lending.rejected.amount)} icon={CircleAlert} to="/admin/peso-a-peso/solicitudes?estatus=rejected" />
            <StatTile label="Revisores" value={counts.lenders} hint="accesos activos" icon={Users} to="/admin/peso-a-peso/aliados" />
          </div>
        </section>

        <section className="overview-block" aria-labelledby="trips-title">
          <div className="section-heading small"><div><span className="page-eyebrow">Viajes</span><h2 id="trips-title">Solicitudes y Apartados</h2></div></div>
          <div className="stat-grid">
            <StatTile label="Solicitudes por atender" value={counts.open_requests} hint="enviadas o en revisión" icon={FileSearch} tone={counts.open_requests > 0 ? "alert" : "default"} to="/admin/solicitudes?status=open" />
            <StatTile label="Apartados activos" value={counts.active_apartados} hint="fondos destinados a viajes" icon={Wallet} to="/admin/apartados" />
            <StatTile label="Viajes publicados" value={counts.published_trips} icon={Handshake} to="/admin/viajes" />
            <StatTile label="Órdenes de fondo en revisión" value={counts.investment_review} icon={AlertTriangle} tone={counts.investment_review > 0 ? "danger" : "ok"} to="/admin/apartados" />
          </div>
        </section>
      </div>

      <div className="overview-grid wide">
        <section className="card">
          <div className="section-heading small"><div><span className="page-eyebrow">Flujos</span><h2>Depósitos y retiros · 14 días</h2></div><span>Movimientos confirmados, MXN</span></div>
          <FlowChart points={series} label="Depósitos y retiros confirmados por día en los últimos catorce días" />
        </section>
        <section className="card">
          <div className="section-heading small"><div><span className="page-eyebrow">Bitácora</span><h2>Actividad reciente</h2></div><Link to="/admin/bitacora" className="text-link">Ver todo <ChevronRight size={14} aria-hidden="true" /></Link></div>
          <AuditList events={recentAudit} />
        </section>
      </div>

      <section>
        <span className="rail-title">Accesos rápidos</span>
        <div className="quick-links">
          <Link to="/admin/fotos" className="quick-link"><Images size={18} aria-hidden="true" /><span><strong>Fotos</strong><small>{counts.media} publicadas</small></span></Link>
          <Link to="/admin/destinos" className="quick-link"><Map size={18} aria-hidden="true" /><span><strong>Destinos</strong><small>{counts.destinations} activos</small></span></Link>
          <Link to="/admin/productos" className="quick-link"><Package size={18} aria-hidden="true" /><span><strong>Productos</strong><small>Tarjetas de Finanzas</small></span></Link>
          <Link to="/admin/ajustes" className="quick-link"><Settings size={18} aria-hidden="true" /><span><strong>Ajustes</strong><small>Tasa, textos, acceso</small></span></Link>
        </div>
      </section>
    </div>
  );
}

function AttentionRow({ item }: { item: AttentionItem }) {
  const Icon = SEVERITY_ICON[item.severity];
  return (
    <li>
      <Link to={item.to} className={`attention-row ${item.severity}`}>
        <span className="attention-icon"><Icon size={16} aria-hidden="true" /></span>
        <span className="attention-copy"><strong>{item.title}</strong><span>{item.detail}</span></span>
        <ChevronRight size={16} aria-hidden="true" className="attention-chevron" />
      </Link>
    </li>
  );
}
